import type { ISbStoryData } from "@storyblok/react";
import {
  getStoryblokApi,
  StoryblokComponent,
  useStoryblokState,
} from "@storyblok/react";
import type {
  GetStaticPaths,
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from "next";
import { useRouter } from "next/router";
import { BreadcrumbJsonLd, NextSeo } from "next-seo";

import type { Blok } from "@/bloks/Page";
import PLP from "@/components/PLP";
import config from "@/config";
import { DEFAULT_LOCALE, SITE_ORIGIN } from "@/features/i18n/i18n.config.mjs";
import { I18nHead } from "@/features/i18n/I18nHead";
import { PATH_REDIRECTS } from "@/features/redirects/redirects.config.mjs";
import { fetchServerTranslations } from "@/i18n.server";
import getCategoryId from "@/lib/centra/getCategoryId";
import { getAllStories } from "@/lib/centra/useStories/getStories";
import { getSubcategories } from "@/lib/hooks/useSubcategories";
import fetchSiteSettings from "@/lib/storyblok/fetchSiteSettings";
import getStory from "@/lib/storyblok/getStory";

type Breadcrumb = { name: string; path: string };

const humanizeSlug = (slug: string) =>
  slug
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const buildContentBreadcrumbs = async (
  slugSegments: string[]
): Promise<Breadcrumb[]> => {
  if (!slugSegments?.length) return [];

  try {
    const storyBlokApi = getStoryblokApi();
    const { data } = await storyBlokApi.get("cdn/links", {
      starts_with: "info-pages/",
      version: "published",
    });
    const links = (data?.links ?? {}) as Record<
      string,
      { name?: string; real_path?: string; is_folder?: boolean }
    >;

    const pathToName: Record<string, string> = {};
    Object.values(links).forEach((link) => {
      if (link.is_folder || !link.real_path || !link.name) return;
      const path = link.real_path.replace(/^\/+/, "").replace(/\/+$/, "");
      pathToName[path] = link.name;
    });

    return slugSegments.map((segment, index) => {
      const path = slugSegments.slice(0, index + 1).join("/");
      return {
        name: pathToName[path] ?? humanizeSlug(segment),
        path: `/${path}`,
      };
    });
  } catch {
    return slugSegments.map((segment, index) => ({
      name: humanizeSlug(segment),
      path: `/${slugSegments.slice(0, index + 1).join("/")}`,
    }));
  }
};

type Retailer = ISbStoryData<{ country: string }>;
const ContentType = ({ story, staticProps }: any) => {
  if (story?.content?.component === "Category" || story === null) {
    return (
      <PLP
        story={story!}
        subcategories={staticProps?.subcategories}
        categoryIds={staticProps?.cId !== null ? staticProps.cId : undefined}
      />
    );
  }
  return <StoryblokComponent blok={story.content} restProps={staticProps} />;
};

const Info: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  story: initialStory,
  staticProps: staticProps,
  breadcrumbs,
  // preview,
}) => {
  const story = useStoryblokState(initialStory!, {});
  const seo = story?.content?.metadata;
  const { locale } = useRouter();
  const origin = SITE_ORIGIN;
  const localePrefix = locale && locale !== DEFAULT_LOCALE ? `/${locale}` : "";

  const breadcrumbItems =
    breadcrumbs && breadcrumbs.length > 0
      ? [
          {
            position: 1,
            name: "Home",
            item: `${origin}${localePrefix}/`,
          },
          ...breadcrumbs.map((crumb, index) => ({
            position: index + 2,
            name: crumb.name,
            item: `${origin}${localePrefix}${crumb.path}`,
          })),
        ]
      : null;

  return (
    <>
      <I18nHead story={story} />

      {story?.content && (
        <NextSeo
          title={seo?.title || story?.name || ""}
          description={seo?.description || ""}
          openGraph={{
            title: seo?.og_title || seo?.title || "",
            description:
              seo?.og_description ||
              seo?.description ||
              (story.content.abstract as string) ||
              "",
            images: seo?.og_image ? [{ url: seo?.og_image }] : [{ url: "" }],
          }}
        />
      )}

      {breadcrumbItems && (
        <BreadcrumbJsonLd itemListElements={breadcrumbItems} />
      )}

      <ContentType story={story} staticProps={staticProps} />
    </>
  );
};

export default Info;

export const getStaticProps = async ({
  params,
  preview = false,
  locale = "en",
}: GetStaticPropsContext) => {
  let info = params?.info as string[];

  if (info && info[0] === "info-pages") {
    info = info.slice(1);
  }

  const slug = info ? (info as string[]).join("/") : "";

  const pathRedirect = PATH_REDIRECTS[`/${slug}`];
  if (pathRedirect) {
    const localePrefix = locale === DEFAULT_LOCALE ? "" : `/${locale}`;
    return {
      redirect: {
        destination: `${localePrefix}${pathRedirect.destination}`,
        permanent: pathRedirect.permanent,
      },
    };
  }

  let story = await getStory<Blok>({
    slug,
    preview,
    locale,
  });

  if (!story?.content) {
    story = await getStory<Blok>({
      slug,
      folder: "info-pages",
      preview,
      locale,
    });
  }
  if (!story?.content) {
    story = await getStory<Blok>({
      slug,
      folder: "category",
      preview,
      locale,
    });
  }
  const staticProps = {} as any;
  let category = false;

  if (story?.content?.component === "Category" || !story?.content) {
    if (!story?.content) {
      story = undefined;
    }
    const categoryArray = params?.info as string[];
    const subcategoriesPromise = getSubcategories(categoryArray, locale);
    const categoryIdPromise = getCategoryId(categoryArray, locale);
    const defaultCategoryIdPromise = getCategoryId(categoryArray, locale);

    const [subcategories, categoryId, defaultId] = await Promise.all([
      subcategoriesPromise,
      categoryIdPromise,
      defaultCategoryIdPromise,
    ]);
    if (subcategories) {
      staticProps.subcategories = subcategories;
    }
    const cId = categoryId || defaultId;
    if (cId) {
      staticProps.cId = [cId];
      category = true;
    }
  }

  if (story?.content?.body?.find((blok) => blok.component === "retailers")) {
    const { stories: initialRetailers } = await getAllStories<Retailer>(
      "retailer",
      preview
    );

    const filterField = "country";
    const options = (initialRetailers &&
      (initialRetailers
        .map(
          (story) =>
            (filterField in story?.content && story?.content[filterField]) || ""
        )
        .filter((value, index, self) => self.indexOf(value) === index)
        .filter(Boolean)
        .sort((a, b) => (a > b ? 1 : -1)) as string[])) ?? [""];
    story?.content?.body.forEach((blok, key) => {
      if (blok.component === "retailers") {
        const blokData = story?.content?.body?.[key];
        blokData.initialRetailers = initialRetailers;
        blokData.options = options;
        story.content.body[key] = blokData;
      }
    });
  }

  const isCategoryPage = category || story?.content?.component === "Category";
  const breadcrumbs: Breadcrumb[] =
    !isCategoryPage && story?.content && info?.length
      ? await buildContentBreadcrumbs(info)
      : [];

  const siteSettings = await fetchSiteSettings(locale, preview);

  const localePaths: Record<string, string> | null = (() => {
    if (!story) return null;
    const stripFolders = (p: string) =>
      p.replace(/^\/?info-pages\//, "/").replace(/^\/?category\//, "/");
    const ensureLeading = (p: string) => (p.startsWith("/") ? p : `/${p}`);
    const map: Record<string, string> = {};
    if (story.default_full_slug) {
      map[DEFAULT_LOCALE] = stripFolders(
        ensureLeading(story.default_full_slug)
      );
    } else if (story.full_slug) {
      map[locale] = stripFolders(ensureLeading(story.full_slug));
    }
    if (story.translated_slugs) {
      for (const t of story.translated_slugs) {
        if (t.path) map[t.lang] = stripFolders(ensureLeading(t.path));
      }
    }
    if (!map[locale] && story.full_slug) {
      map[locale] = stripFolders(ensureLeading(story.full_slug));
    }
    return Object.keys(map).length ? map : null;
  })();

  return {
    notFound: !story?.content && !category,
    props: {
      ...(await fetchServerTranslations(locale)),
      story,
      preview,
      staticProps,
      breadcrumbs,
      siteSettings,
      localePaths,
    },
    revalidate: config.revalidate,
  };
};

export const getStaticPaths: GetStaticPaths = async ({}) => {
  const storyBlokApi = getStoryblokApi();
  const { data } = await storyBlokApi.get("cdn/links", {
    starts_with: "info-pages/",
    version: "published",
  });
  const links = data.links ?? {};
  const paths = Object.values(links)
    .filter((link) => !link.is_folder)
    .map((link) => {
      const slug = (link.real_path ?? "").replaceAll("//", "/");
      const realSlug = (slug.startsWith("/") ? slug.slice(1) : slug).split("/");
      return { params: { info: [...realSlug] } };
    });

  return {
    paths,
    fallback: "blocking",
  };
};
